/**
 * Service-worker registration (card spotlight-046).
 *
 * vite-plugin-pwa exposes a `virtual:pwa-register` module that
 * handles SW registration + lifecycle callbacks.  This module
 * imports it and registers the SW as a side effect at module
 * load — main.jsx just needs a single `import './sw-register.js'`
 * line.
 *
 * Why a side-effect import (and not an exported `setupServiceWorker`
 * function the caller invokes)?  main.jsx is shared with card 045's
 * nav-IA rework, so every additional line in main.jsx is a merge
 * conflict waiting to happen.  Side-effect import keeps the touch
 * to exactly one line.
 *
 * Strategy: `registerType: 'autoUpdate'` (card 101) means the new SW
 * activates as soon as it lands; the next render cycle reloads.  No
 * prompt, no toast.  Operators noted the "refresh to update" line
 * felt like the dashboard half-shipping a deploy.
 *
 * Card 110 — `autoUpdate` still leaves a gap: a tab held open across
 * a deploy (e.g. operator left an editor mid-save overnight) can land
 * with `onNeedRefresh` instead of the same-tick activate-and-reload.
 * Auto-reloading those tabs would discard in-flight work, so we
 * surface a non-intrusive banner via the `<UpdateAvailableBanner>`
 * chrome instead.  This module exposes a tiny pub/sub the banner
 * subscribes to, plus the existing `updateSW` for its Reload CTA.
 */
import { registerSW } from 'virtual:pwa-register';

// Subscriber list for "an SW update is waiting".  Module-level so the
// pub/sub state is shared across the React tree without a context —
// the banner is the only consumer today, but keeping it as a list
// means a future toast / diagnostics panel can subscribe without
// reaching into virtual:pwa-register itself.
const updateAvailableListeners = new Set();
let updateAvailable = false;

function notifyUpdateAvailable() {
  updateAvailable = true;
  for (const fn of updateAvailableListeners) {
    try {
      fn(true);
    } catch (err) {
      // Subscriber errors must not break the SW callback chain.
      // eslint-disable-next-line no-console
      console.warn('[spotlight] update-available listener threw', err);
    }
  }
}

const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    // Card 110 — autoUpdate handles the common case (activate +
    // reload on the next render).  This callback fires for the edge
    // cases autoUpdate can't auto-resolve: a tab held open across
    // multiple deploys, or one whose render loop hasn't ticked since
    // the SW activated.  Forcing `updateSW(true)` here would discard
    // unsaved work in those tabs (the user might be mid-edit).
    // Instead, flip the flag so <UpdateAvailableBanner> renders a
    // gentle "A new version is available — Reload" banner; the
    // operator decides when to refresh.
    notifyUpdateAvailable();
  },
  onOfflineReady() {
    // App shell is cached; the dashboard works offline-for-cached
    // routes from this point.  No UI surface today.
  },
  onRegisterError(err) {
    // Non-fatal — the dashboard still works without the SW.
    // Surface the failure to the console for debugging.
    // eslint-disable-next-line no-console
    console.warn('[spotlight] service worker registration failed', err);
  },
});

/**
 * Subscribe to "an SW update is waiting" notifications.  The callback
 * receives `true` when the update lands.  If the update has already
 * fired by the time the subscriber mounts (e.g. SW activated before
 * React rendered the banner), the callback is invoked synchronously
 * with the current state so the banner appears on first paint.
 *
 * Returns an unsubscribe function — the caller invokes it from the
 * React effect cleanup so a banner unmount doesn't keep a stale
 * closure reachable from the module-level Set.
 */
export function subscribeToUpdateAvailable(cb) {
  updateAvailableListeners.add(cb);
  if (updateAvailable) {
    // Replay current state for late subscribers.
    try {
      cb(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[spotlight] update-available subscriber threw', err);
    }
  }
  return () => {
    updateAvailableListeners.delete(cb);
  };
}

// Re-export updateSW so the banner's Reload CTA can trigger an
// immediate activate-and-reload without re-importing
// virtual:pwa-register itself (which has no public re-register
// API).
export { updateSW };
